import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal, { ModalActions, ModalTitle, ModalBody } from '@strava/ui/Modal';
import Button from '@strava/ui/Button';
import Tooltip from '@strava/ui/Tooltip';
import CryptoJS from 'crypto-js';
import NavigationHelpNormalXsmall from '@strava/icons/NavigationHelpNormalXsmall';
import I18n from 'utils/I18n';
import { Markup } from 'interweave';
import { authorizationUrlsProps } from '../../shared/prop-types';

import styles from './index.scss';
import DisclaimerText from '../DisclaimerText';
import DropdownDatePicker from '../../../../../../components/shared/DropdownDatePicker';

const SignupAgeGateModal = ({
  open,
  signupOption,
  authorizationUrls,
  onDismissCallback,
  locale
}) => {
  const [dob, setDob] = useState({ month: '', day: '', year: '' });
  const [erroredControls, setErroredControls] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);

  const I18nPrefix = 'strava.landing_page';

  const copyCantMakeAccount = I18n.t(`${I18nPrefix}.cant_make_account`);

  const isCompleteDate = ({ month, day, year }) => {
    let result = true;
    if (month === null || month === '') {
      setErroredControls((prev) => [...prev, 'month']);
      result = false;
    }
    if (day === null || day === '') {
      setErroredControls((prev) => [...prev, 'day']);
      result = false;
    }
    if (year === null || year === '') {
      setErroredControls((prev) => [...prev, 'year']);
      result = false;
    }
    return result;
  };

  /**
   * Athlete must be >= 13 years to create an account
   */
  const isAllowedAge = ({ month, day, year }) => {
    const inputDate = new Date(year, month - 1, day); // Jan => 1 for input UI
    const today = new Date();
    const minDate = new Date(
      today.getFullYear() - 13,
      today.getMonth(),
      today.getDate()
    );
    return inputDate < minDate;
  };

  const remove = (arr, value) => {
    let i = 0;
    while (i < arr.length) {
      if (arr[i] === value) {
        arr.splice(i, 1);
      } else {
        i += 1;
      }
    }
    return arr;
  };

  const updateErrorState = ({ month, day, year }) => {
    if (month) {
      setErroredControls((prev) => remove(prev, 'month'));
    }
    if (day) {
      setErroredControls((prev) => remove(prev, 'day'));
    }
    if (year) {
      setErroredControls((prev) => remove(prev, 'year'));
    }
  };

  const handleSignup = () => {
    const { month, day, year } = dob;
    if (!isCompleteDate({ month, day, year })) {
      return; // block signup process
    }
    if (!isAllowedAge({ month, day, year })) {
      setErrorMessage(copyCantMakeAccount);
      return; // block signup process
    }

    window.localStorage.setItem(
      'on-sig-d',
      CryptoJS.AES.encrypt(
        `${day}-${month}-${year}`,
        process.env.CRYPTO_JS_SECRET
      ).toString()
    );
    window.location = authorizationUrls[signupOption];
  };

  const handleDobChange = (d) => {
    setDob(d);
    updateErrorState(d);
    setErrorMessage(null);
  };

  return (
    <Modal
      className={styles.modal}
      isOpen={open}
      onDismiss={onDismissCallback}
      hasClose={true}
      aria-labelledby="sign-up-modal-title"
    >
      <ModalTitle className={styles.modalTitle} id="sign-up-modal-title">
        {I18n.t(`${I18nPrefix}.before_you_sign_up`)}
      </ModalTitle>
      <ModalBody className={styles.modalBody}>
        <div className={styles.subtitle}>
          <div className={styles.text}>{I18n.t(`${I18nPrefix}.birthday`)}</div>
          <Tooltip
            label={
              <Markup noWrap={true} content={I18n.t(`${I18nPrefix}.why_dob`)} />
            }
            position="top"
            className={styles.toolTip}
          >
            <NavigationHelpNormalXsmall />
          </Tooltip>
        </div>

        <DropdownDatePicker
          className={styles.datepicker}
          dateChangeCallback={handleDobChange}
          erroredControls={erroredControls}
          locale={locale}
        />

        <DisclaimerText showLoginOption={false} />

        {errorMessage && (
          <div
            data-testid="sign-up-modal-error-message"
            className={styles.errorMessage}
          >
            {errorMessage}
          </div>
        )}
      </ModalBody>
      <ModalActions className={styles.modalActions}>
        <Button
          type="button"
          variant="primary"
          className={styles.signupButton}
          onClick={handleSignup}
          data-testid="sign-up-age-gate-submit"
        >
          {I18n.t(`${I18nPrefix}.agree_and_signup`)}
        </Button>
      </ModalActions>
    </Modal>
  );
};

SignupAgeGateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  signupOption: PropTypes.oneOf(['facebook', 'google', 'email']),
  authorizationUrls: authorizationUrlsProps.isRequired,
  onDismissCallback: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired
};

SignupAgeGateModal.defaultProps = {
  signupOption: null
};

export default SignupAgeGateModal;
